import { useEffect, useState } from "react";
import { useSite } from "../../lib/site";
import { ContactForm } from "./ContactForm";
import { StyledText } from "./StyledText";

export function SloganBand() {
  const { site, t } = useSite();
  const text = t(site.campaign?.ticker) || t(site.campaign?.slogan);
  const parts = text.split("•").map((s) => s.trim()).filter(Boolean);
  const row = [...parts, ...parts, ...parts];
  return (
    <div data-testid="slogan-band" className="bg-s-primary text-s-onprimary py-5 overflow-hidden -rotate-1 my-6">
      <div className="flex w-max marquee">
        {[0, 1].map((k) => (
          <div key={k} className="flex shrink-0">
            {row.map((p, i) => (
              <span key={i} className="font-display font-black uppercase text-2xl sm:text-3xl px-8 whitespace-nowrap">{p} <span className="opacity-50 ml-8">✦</span></span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function WhyRunning() {
  const { site, t, ui } = useSite();
  return (
    <section data-testid="section-neden-aday-oldum" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-[1fr_1.6fr] gap-12">
      <div>
        <p className="eyebrow mb-4">01</p>
        <StyledText as="h2" className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-none" style={site.profile?.section_style} text={ui.whyRunning} />
      </div>
      <blockquote className="text-xl sm:text-2xl leading-relaxed border-l-4 border-site-primary pl-8 whitespace-pre-line">
        {t(site.campaign?.why)}
      </blockquote>
    </section>
  );
}

export function Priorities() {
  const { site, t, ui } = useSite();
  const items = site.campaign?.priorities || [];
  if (!items.length) return null;
  return (
    <section data-testid="priorities-bento-grid" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
      <p className="eyebrow mb-4">02</p>
      <StyledText as="h2" className="font-display font-black uppercase text-4xl sm:text-5xl mb-12" style={site.profile?.section_style} text={ui.priorities} />
      <div className="grid md:grid-cols-6 gap-4">
        {items.map((p, i) => (
          <div key={i} data-testid={`priority-card-${i}`}
            className={`rounded-3xl p-8 border border-site-border transition-transform hover:-translate-y-1 ${i === 0 ? "md:col-span-4 bg-s-primary text-s-onprimary" : i === 3 ? "md:col-span-4 bg-s-surface" : "md:col-span-2 bg-s-surface"}`}>
            <span className="font-display font-black text-5xl opacity-30">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-display font-bold text-2xl mt-6">{t(p.title)}</h3>
            <p className={`mt-3 ${i === 0 ? "opacity-90" : "text-s-muted"}`}>{t(p.text)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const diff = (target) => {
  const ms = Math.max(0, new Date(target).getTime() - Date.now());
  return [Math.floor(ms / 864e5), Math.floor((ms / 36e5) % 24), Math.floor((ms / 6e4) % 60), Math.floor((ms / 1e3) % 60)];
};

export function Countdown({ compact }) {
  const { site, ui } = useSite();
  const date = site.campaign?.election_date;
  const [v, setV] = useState(() => diff(date));
  useEffect(() => { const id = setInterval(() => setV(diff(date)), 1000); return () => clearInterval(id); }, [date]);
  if (!date) return null;
  const labels = [ui.days, ui.hours, ui.minutes, ui.seconds];
  const boxes = (
    <div className="flex gap-3 sm:gap-4">
      {v.map((n, i) => (
        <div key={i} className={`rounded-2xl border border-site-border bg-s-surface text-center ${compact ? "px-3 py-2 min-w-[64px]" : "px-5 py-5 min-w-[90px] sm:min-w-[130px]"}`}>
          <div className={`font-display font-black tabular-nums ${compact ? "text-2xl" : "text-4xl sm:text-6xl"}`}>{String(n).padStart(2, "0")}</div>
          <div className="text-[0.65rem] uppercase tracking-widest text-s-muted mt-1">{labels[i]}</div>
        </div>
      ))}
    </div>
  );
  if (compact) return <div data-testid="hero-countdown"><p className="eyebrow mb-3">{ui.countdown}</p>{boxes}</div>;
  return (
    <section data-testid="election-countdown-timer" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 flex flex-col items-center text-center">
      <p className="eyebrow mb-4">03</p>
      <StyledText as="h2" className="font-display font-black uppercase text-4xl sm:text-5xl mb-10" style={site.profile?.section_style} text={ui.countdown} />
      {boxes}
    </section>
  );
}

export function VolunteerSection() {
  const { site, ui } = useSite();
  return (
    <section id="gonullu" data-testid="volunteer-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
      <div className="rounded-[2rem] bg-s-surface border border-site-border p-8 sm:p-14 grid lg:grid-cols-2 gap-12">
        <div>
          <p className="eyebrow mb-4">04</p>
          <StyledText as="h2" className="font-display font-black uppercase text-4xl sm:text-5xl leading-none" style={site.profile?.section_style} text={ui.joinUs} />
          <p className="mt-6 text-lg text-s-muted">{ui.volunteerText}</p>
        </div>
        <ContactForm type="volunteer" />
      </div>
    </section>
  );
}
